import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { ordersAPI } from '../lib/api';
import { Truck, MapPin } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface Delivery {
  id: string;
  _id: string;
  orderId: string;
  status: string;
  deliveryAddress: any;
  createdAt: string;
  totalAmount: number;
  userId?: {
    fullName: string;
    name: string;
    phone: string;
  };
}

const Delivery = () => {
  const navigate = useNavigate();
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchDeliveries();
  }, []);

  const fetchDeliveries = async () => {
    try {
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : {};
      const response = await (user.role === 'delivery' ? ordersAPI.getMyDeliveries() : ordersAPI.getAll());

      const data = response.data || [];
      if (user.role === 'delivery') {
        // Filter for active/upcoming deliveries for the agent
        setDeliveries(data.filter((o: any) =>
          ['assigned', 'confirmed', 'preparing', 'ready_for_pickup', 'out_for_delivery'].includes(o.status)
        ));
      } else {
        setDeliveries(data);
      }
    } catch (error) {
      console.error('Fetch deliveries error:', error);
      toast.error('Failed to load deliveries');
    } finally {
      setLoading(false);
    }
  };

  const getAddressString = (address: any) => {
    if (typeof address === 'string') return address;
    if (address?.fullAddress) return address.fullAddress;
    if (address?.addressLine1) {
      return `${address.addressLine1}${address.city ? `, ${address.city}` : ''}`;
    }
    return 'No address provided';
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'assigned':
        return 'bg-blue-100 text-blue-800';
      case 'in_transit':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Delivery Routes</h1>
        <p className="text-gray-500">Track your deliveries</p>
      </div>

      {deliveries.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl">
          <Truck className="mx-auto text-gray-300 mb-4" size={64} />
          <p className="text-gray-500">No active deliveries</p>
        </div>
      ) : (
        <div className="space-y-4">
          {deliveries.map((delivery) => (
            <div key={delivery.id || delivery._id} className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Truck className="text-green-600" size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">
                      Order #{delivery.orderId.slice(-6).toUpperCase()}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(delivery.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(delivery.status)}`}>
                  {delivery.status.replace('_', ' ')}
                </span>
              </div>

              <div className="flex items-start gap-2 text-sm text-gray-600 mb-3">
                <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                <span>{getAddressString(delivery.deliveryAddress)}</span>
              </div>

              {delivery.userId && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-sm text-gray-800">
                    <span className="font-medium">Customer:</span> {delivery.userId.fullName || delivery.userId.name}
                  </p>
                  <p className="text-sm text-gray-500">{delivery.userId.phone}</p>
                </div>
              )}

              <div className="mt-3 flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-800">₹{delivery.totalAmount}</p>
                <button
                  onClick={() => navigate(`/orders/${delivery._id || delivery.id}`)}
                  className="text-xs text-green-600 font-semibold hover:underline"
                >
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
};

export default Delivery;